import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';

import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterModule, ActivatedRoute } from '@angular/router';
import { takeReturnUrl } from '../../utils/return-url';
import { AuthService } from '../../services/auth.service';
import { GoogleAuthService } from '../../services/google-auth.service';
import { LoggerService } from '../../services/logger.service';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideEye,
  lucideEyeOff,
  lucideInfo,
  lucideMail,
  lucideTriangleAlert,
  lucideUsers,
} from '@ng-icons/lucide';
import { HlmAlertImports } from '@spartan-ng/helm/alert';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmFieldImports } from '@spartan-ng/helm/field';
import { HlmInputImports } from '@spartan-ng/helm/input';
import { HlmInputGroupImports } from '@spartan-ng/helm/input-group';
import { HlmSeparatorImports } from '@spartan-ng/helm/separator';
import { HlmSpinnerImports } from '@spartan-ng/helm/spinner';
import { HlmTypographyImports } from '@spartan-ng/helm/typography';
import { LogoComponent } from '../../components/logo/logo.component';
import { lastValueFrom } from 'rxjs';

@Component({
  selector: 'app-login',
  standalone: true,
  templateUrl: './login.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ReactiveFormsModule,
    RouterModule,
    NgIcon,
    HlmAlertImports,
    HlmButtonImports,
    HlmCardImports,
    HlmFieldImports,
    HlmInputImports,
    HlmInputGroupImports,
    HlmSeparatorImports,
    HlmSpinnerImports,
    HlmTypographyImports,
    LogoComponent,
  ],
  providers: [
    provideIcons({
      lucideEye,
      lucideEyeOff,
      lucideInfo,
      lucideMail,
      lucideTriangleAlert,
      lucideUsers,
    }),
  ],
})
export class LoginComponent implements OnInit, OnDestroy {
  loginForm: FormGroup;
  submitted = false;
  loading = false;
  error = '';
  showResendVerification = false;
  resendLoading = false;
  resendSuccess = false;
  showPassword = false;
  redirectMessage = '';
  pendingGroupName: string | null = null;
  redirectParam: string | null = null;
  googleAvailable = false;
  // Google credential without a matching account: user chooses create vs. link
  pendingGoogleCredential: string | null = null;
  showGoogleChoice = false;
  googleLinkMode = false;
  private renderedGoogleBtnEl: HTMLElement | null = null;
  private disposeGoogleBtn: (() => void) | null = null;

  // Setter instead of ngAfterViewInit: the form (and the button container in it)
  // is destroyed while the create-or-link choice is shown and re-created afterwards
  @ViewChild('googleBtn') set googleBtn(el: ElementRef<HTMLElement> | undefined) {
    if (el && el.nativeElement !== this.renderedGoogleBtnEl) {
      this.renderedGoogleBtnEl = el.nativeElement;
      this.renderGoogleButton(el.nativeElement);
    }
  }

  constructor(
    private formBuilder: FormBuilder,
    private authService: AuthService,
    private googleAuthService: GoogleAuthService,
    private router: Router,
    private route: ActivatedRoute,
    private logger: LoggerService,
  ) {
    this.loginForm = this.formBuilder.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', Validators.required],
    });
  }

  ngOnInit() {
    this.pendingGroupName = this.route.snapshot.queryParamMap.get('groupName');
    this.redirectParam = this.route.snapshot.queryParamMap.get('redirect');

    // Check if user is already logged in
    if (this.authService.isLoggedIn()) {
      const redirect = takeReturnUrl(this.redirectParam);

      // Set feedback message and redirect after a short delay
      this.redirectMessage = 'Willkommen zurück!';

      // Use setTimeout to ensure the message is shown before redirect
      setTimeout(() => {
        // A requested page first: one that needs a finished profile sends to onboarding
        const navigation =
          redirect || this.authService.isProfileCompleted()
            ? this.router.navigateByUrl(redirect || '/')
            : this.router.navigate(['/onboarding']);

        // A guard can reject the stored session and send us back here. That is the same
        // URL, so the router ignores it and this component is reused — without this the
        // redirect spinner would stay forever instead of showing the form.
        navigation
          .then((navigated) => {
            if (!navigated) this.redirectMessage = '';
          })
          .catch(() => (this.redirectMessage = ''));
      }, 1500); // 1.5 second delay to show the message
    }
  }

  ngOnDestroy() {
    this.disposeGoogleBtn?.();
  }

  private async renderGoogleButton(container: HTMLElement) {
    try {
      // The container is re-created around the create-or-link choice
      this.disposeGoogleBtn?.();
      this.disposeGoogleBtn = await this.googleAuthService.renderButton(
        container,
        { text: 'signin_with' },
        (credential) => this.onGoogleCredential(credential),
      );
      this.googleAvailable = true;
    } catch (error) {
      // Blocked script or missing config must never break password login
      this.logger.error('Google Sign-In not available:', error);
    }
  }

  get f() {
    return this.loginForm.controls;
  }

  onGoogleCredential(credential: string) {
    this.error = '';
    this.showResendVerification = false;
    this.resendSuccess = false;
    this.showGoogleChoice = false;
    this.googleLinkMode = false;
    this.loading = true;

    lastValueFrom(this.authService.loginWithGoogle(credential, undefined, 'login'))
      .then(() => this.handleLoginSuccess())
      .catch((error) => {
        this.loading = false;
        if (error.status === 404 && error.error?.detail?.code === 'NO_ACCOUNT_FOR_GOOGLE_LOGIN') {
          // No account yet — let the user choose between creating and linking
          this.pendingGoogleCredential = credential;
          this.showGoogleChoice = true;
          return;
        }
        this.logger.error(error);
        this.error =
          this.detailMessage(error) ||
          'Die Anmeldung mit Google ist fehlgeschlagen. Bitte versuche es erneut.';
      });
  }

  createAccountWithGoogle() {
    if (!this.pendingGoogleCredential) return;
    this.showGoogleChoice = false;
    this.loading = true;
    lastValueFrom(
      this.authService.loginWithGoogle(this.pendingGoogleCredential, undefined, 'register'),
    )
      .then(() => this.handleLoginSuccess())
      .catch((error) => {
        this.logger.error(error);
        this.error =
          this.detailMessage(error) ||
          'Die Registrierung mit Google ist fehlgeschlagen. Bitte versuche es erneut.';
        this.loading = false;
      });
  }

  // detail is display-ready German text on most errors, but can be a
  // structured object (e.g. the no-account code) — only render strings
  private detailMessage(error: any): string | null {
    const detail = error?.error?.detail;
    return typeof detail === 'string' ? detail : null;
  }

  startGoogleLinkLogin() {
    this.showGoogleChoice = false;
    this.googleLinkMode = true;
  }

  cancelGoogleFlow() {
    this.showGoogleChoice = false;
    this.googleLinkMode = false;
    this.pendingGoogleCredential = null;
  }

  private handleLoginSuccess() {
    const redirect = takeReturnUrl(this.route.snapshot.queryParams['redirect']);

    // A requested page first: one that needs a finished profile sends to onboarding
    if (!redirect && !this.authService.isProfileCompleted()) {
      this.logger.log('Redirecting to onboarding');
      this.router.navigate(['/onboarding']);
    } else {
      this.logger.log('Redirecting to', redirect || '/');
      this.router.navigateByUrl(redirect || '/');
    }
  }

  onSubmit() {
    this.submitted = true;
    this.error = '';
    this.showResendVerification = false;
    this.resendSuccess = false;

    if (this.loginForm.invalid) {
      return;
    }

    this.loading = true;

    lastValueFrom(
      this.authService.login({
        username: this.loginForm.value.email,
        password: this.loginForm.value.password,
      }),
    )
      .then(async () => {
        if (this.googleLinkMode && this.pendingGoogleCredential) {
          try {
            await lastValueFrom(this.authService.linkGoogle(this.pendingGoogleCredential));
          } catch (linkError: any) {
            // Logged in either way — linking can be retried from the settings page
            this.logger.error('Google linking after login failed:', linkError);
          }
          this.pendingGoogleCredential = null;
          this.googleLinkMode = false;
        }
        this.handleLoginSuccess();
      })
      .catch((error) => {
        this.logger.error(error);
        if (error.status === 403 && error.error?.detail?.includes('E-Mail-Adresse')) {
          this.error = error.error.detail;
          this.showResendVerification = true;
        } else {
          this.error = error.error?.detail || 'Die Email oder das Passwort ist ungültig.';
        }
        this.loading = false;
      });
  }

  resendVerificationEmail() {
    this.resendLoading = true;
    this.resendSuccess = false;

    this.authService.resendVerificationEmail(this.loginForm.value.email).subscribe({
      next: () => {
        this.resendSuccess = true;
        this.resendLoading = false;
      },
      error: (error) => {
        this.logger.error('Error resending verification email:', error);
        this.error = 'Fehler beim Senden der Bestätigungs-E-Mail. Bitte versuche es später erneut.';
        this.resendLoading = false;
      },
    });
  }

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }
}
